// Custom hook: global keydown handler for ProductionOrder
import { useEffect } from "react";
import {
  savingOtherChargesData,
  savingRemarks,
} from "./productionStorage";

const useProductionKeyboard = ({
  handlers,
  // State values
  shortcutsListModalOpen,
  setShortcutsListModalOpen,
  hasReverseOrderData,
  setTempQuantityInputValue,
  isVisible,
  setIsVisible,
  setSelectedRowKey,
  setShowInputFields,
  setSelectedQuantity,
  setSelectedTotalQuantity,
  editingValue,
  setEditingValue,
  originalValue,
  QtyShowInputFields,
  setQtyShowInputFields,
  searchedProductsData,
  selectedRowKey,
  disableSearch,
  chooseProduct,
  chooseProductsData,
  setSelectedProductIndex,
  selectedProductIndex,
  otherCharges,
  setOtherCharges,
  showRemarks,
  setShowRemarks,
  tempRemarks,
  setTempRemarks,
  setRemarks,
  remarks,
  tempOtherChargesData,
  setOtherChargesData,
  selectedProduct,
  tillLink,
  setTillLink,
  isLocked,
  setIsLocked,
  showOrderHistory,
  setShowOrderHistory,
  totalSalePrice,
  // Refs
  plusButtonRef,
  pricingMethodRef,
  qtyUnitInputRef,
  inputSearchRef,
  // Helpers
  calculateColumns,
  scrollToProduct,
}) => {
  const handleKey = (e) => {
    const { shiftKey, keyCode, ctrlKey } = e;

    if (shortcutsListModalOpen && (keyCode === 38 || keyCode === 40)) return;

    if (shiftKey && keyCode === 189) {
      if (!hasReverseOrderData) {
        setTempQuantityInputValue((prevValue) => {
          const newQuantity = Math.max(1, parseInt(prevValue) - 1);
          handlers.updateQuantityAndProducts(newQuantity);
          return newQuantity;
        });
      }
    }

    if (shiftKey && (keyCode === 187 || keyCode === 107)) {
      if (!hasReverseOrderData) {
        setTempQuantityInputValue((prevValue) => {
          const newQuantity = Math.max(1, parseInt(prevValue) + 1);
          handlers.updateQuantityAndProducts(newQuantity);
          return newQuantity;
        });
      }
    }

    if (keyCode === 9) {
      if (isVisible) {
        e.preventDefault();
        e.stopPropagation();
        setIsVisible(false);
        setSelectedRowKey(null);
        setShowInputFields(false);
        setSelectedQuantity(0);
        setSelectedTotalQuantity(0);
        setEditingValue(originalValue);
      }
      if (shortcutsListModalOpen) setShortcutsListModalOpen(false);
      if (QtyShowInputFields) {
        setQtyShowInputFields(false);
        setTimeout(() => { if (plusButtonRef.current) plusButtonRef.current.focus(); }, 0);
      }
    }

    if (keyCode === 38 && !hasReverseOrderData && !disableSearch) {
      setSelectedRowKey((prevKey) => {
        const currentIndex = searchedProductsData.findIndex((item) => item.key === prevKey);
        const newIndex = (currentIndex - 1 + searchedProductsData.length) % searchedProductsData.length;
        return searchedProductsData[newIndex]?.key;
      });
    }

    if (keyCode === 40 && !hasReverseOrderData && !disableSearch) {
      setSelectedRowKey((prevKey) => {
        const currentIndex = searchedProductsData.findIndex((item) => item.key === prevKey);
        const newIndex = (currentIndex + 1) % searchedProductsData.length;
        return searchedProductsData[newIndex]?.key;
      });
    }

    if (keyCode === 46) {
      const selProduct = searchedProductsData.find((item) => item.key === selectedRowKey);
      if (selProduct && !hasReverseOrderData && !disableSearch) {
        handlers.handleDeleteSearchedProduct(selProduct);
      }
    }

    if (shiftKey && keyCode === 67) {
      setShortcutsListModalOpen(true);
      if (plusButtonRef?.current) plusButtonRef?.current.focus();
    }

    if (keyCode === 13) {
      if (document.activeElement === plusButtonRef.current && !hasReverseOrderData) handlers.handleOtherChanges();
      if (otherCharges && pricingMethodRef.current) pricingMethodRef.current.focus();
      if (showRemarks) {
        savingRemarks(tempRemarks);
        setRemarks(tempRemarks);
        setShowRemarks(false);
      }
      if (otherCharges) {
        savingOtherChargesData(tempOtherChargesData);
        setOtherChargesData(tempOtherChargesData);
        setOtherCharges(false);
      }
    }

    if (keyCode === 37 && chooseProduct) {
      if (chooseProductsData?.length > 0) {
        setSelectedProductIndex((prevIndex) => {
          const newIndex = (prevIndex - 1 + chooseProductsData.length) % chooseProductsData.length;
          scrollToProduct(newIndex);
          return newIndex;
        });
      }
    }

    if (keyCode === 39 && chooseProduct) {
      if (chooseProductsData?.length > 0) {
        setSelectedProductIndex((prevIndex) => {
          const newIndex = (prevIndex + 1) % chooseProductsData.length;
          scrollToProduct(newIndex);
          return newIndex;
        });
      }
    }

    const numColumns = calculateColumns();
    if (keyCode === 38 && chooseProduct) {
      setSelectedProductIndex((prevIndex) => {
        const newIndex = (prevIndex - numColumns + chooseProductsData.length) % chooseProductsData.length;
        scrollToProduct(newIndex);
        return newIndex;
      });
    }
    if (keyCode === 40 && chooseProduct) {
      setSelectedProductIndex((prevIndex) => {
        const newIndex = (prevIndex + numColumns) % chooseProductsData.length;
        scrollToProduct(newIndex);
        return newIndex;
      });
    }

    if (keyCode === 13 && selectedProductIndex !== null) {
      const selProduct = chooseProductsData[selectedProductIndex];
      if (selProduct) handlers.handleProductSelect(selProduct)();
    }

    if (e.altKey) {
      if (keyCode === 66) {
        if (chooseProduct || otherCharges || showRemarks || showOrderHistory || isLocked) return;
        setTillLink(true);
      }
      if (keyCode === 85) {
        if (chooseProduct || showOrderHistory || isLocked) return;
        if (chooseProduct || showOrderHistory || otherCharges || showRemarks) return;
        try { handlers.confirmUnlink(); } catch (error) { console.error("Error executing Alt + U shortcut:", error); }
      }
      if (keyCode === 76) {
        if (chooseProduct || tillLink || otherCharges || showRemarks || showOrderHistory) return;
        setIsLocked(true);
      }
      if (keyCode === 84) {
        if (selectedProduct?.productmanufacturing?.[0]?.standard_price === "Y") {
          pricingMethodRef.current.focus();
        }
      }
      if (keyCode === 73) {
        if (showRemarks || otherCharges || tillLink) return;
        if (qtyUnitInputRef.current) {
          qtyUnitInputRef.current.focus();
          qtyUnitInputRef.current.select();
        }
      }
      if (keyCode === 72) {
        if (chooseProduct || tillLink || isLocked) return;
        if (totalSalePrice <= 0) setShowOrderHistory(true);
      }
      if (keyCode === 83) {
        if (chooseProduct || tillLink) return;
        e.preventDefault();
        if (inputSearchRef?.current && !hasReverseOrderData && !disableSearch) inputSearchRef?.current.focus();
      }
      if (keyCode === 67) {
        if (selectedProduct || tillLink || showOrderHistory) return;
        handlers.handleChooseProduct();
      }
      if (keyCode === 79) {
        if (showRemarks || tillLink || showOrderHistory || isLocked) return;
        if (!hasReverseOrderData && selectedProduct) handlers.handleOtherChanges();
      }
      if (keyCode === 77 && selectedProduct) {
        if (otherCharges || tillLink || isLocked) return;
        setShowRemarks(true);
        setTempRemarks(remarks);
      }
      if (keyCode === 80 && selectedProduct) {
        if (otherCharges || showRemarks || tillLink) return;
        handlers.handleProceed();
      }
      if (keyCode === 65) {
        if (otherCharges || showRemarks || chooseProduct || tillLink) return;
        handlers.handleCancel();
      }
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  });
};

export default useProductionKeyboard;
